import { useCallback, useEffect, useState } from 'react';

export interface Stepper {
  index: number;
  playing: boolean;
  speed: number;
  setPlaying: (value: boolean) => void;
  setSpeed: (value: number) => void;
  next: () => void;
  reset: () => void;
}

export function useStepper(total: number, interval = 1200): Stepper {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(!reduced);
  const [speed, setSpeed] = useState(1);

  const next = useCallback(() => setIndex((current) => (current + 1) % total), [total]);
  const reset = useCallback(() => {
    setPlaying(false);
    setIndex(0);
  }, []);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(next, interval / speed);
    return () => window.clearInterval(timer);
  }, [interval, next, playing, speed]);

  return { index, playing, speed, setPlaying, setSpeed, next, reset };
}

export function AnimationControls({ stepper, steps, label = '动画控制' }: { stepper: Stepper; steps: string[]; label?: string }) {
  return (
    <div className="animation-controls" aria-label={label}>
      <div className="control-buttons">
        <button type="button" className="button secondary compact" onClick={() => stepper.setPlaying(!stepper.playing)} aria-label={stepper.playing ? '暂停动画' : '播放动画'}>
          {stepper.playing ? 'Ⅱ 暂停' : '▶ 播放'}
        </button>
        <button type="button" className="button ghost compact" onClick={stepper.reset}>↺ 重新开始</button>
        <button type="button" className="button ghost compact" onClick={() => { stepper.setPlaying(false); stepper.next(); }}>下一步 →</button>
      </div>
      <label className="speed-control">
        <span>速度 {stepper.speed.toFixed(1)}×</span>
        <input aria-label="动画速度" type="range" min="0.5" max="2" step="0.5" value={stepper.speed} onChange={(event) => stepper.setSpeed(Number(event.target.value))} />
      </label>
      <div className="step-readout" role="status">
        <span className="step-count">步骤 {stepper.index + 1}/{steps.length}</span>
        <strong>{steps[stepper.index]}</strong>
      </div>
    </div>
  );
}
